import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { 
  Target, 
  Star, 
  Zap, 
  Crown,
  Check,
  Lock,
  TrendingUp,
  DollarSign,
  BarChart3,
  Video,
  Mic,
  Sparkles,
  Trophy
} from 'lucide-react';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const TIER_ICONS = {
  1: Target,
  2: Star,
  3: Zap,
  4: Crown
};

const FEATURE_ICONS = {
  'static_comics': Target,
  'monetization': DollarSign,
  'analytics': BarChart3,
  'motion_comics': Video,
  'voice_hub': Mic,
  'studio_tools': Sparkles,
  'platform_promotion': TrendingUp,
  'funding_access': Trophy
};

const FEATURE_DESCRIPTIONS = {
  'static_comics': 'Upload traditional comic episodes',
  'monetization': 'Earn from tips and ad revenue',
  'analytics': 'Advanced analytics dashboard',
  'motion_comics': 'Upload MP4/WebM motion comics',
  'voice_hub': 'Access voice actor collaboration portal',
  'studio_tools': 'Professional studio-level tools',
  'platform_promotion': 'Homepage featuring & promotion',
  'funding_access': 'Access to Trap Original Fund'
};

const TierSystemModal = ({ isOpen, onClose, currentTier }) => {
  const [tiers, setTiers] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchTiers();
    }
  }, [isOpen]);

  const fetchTiers = async () => {
    try {
      const response = await axios.get(`${API}/tiers`);
      setTiers(response.data.tiers);
    } catch (error) {
      console.error('Error fetching tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-black text-white">
            Creator Tier System
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Progress through 4 tiers to unlock powerful features and grow your creative empire
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* How It Works */}
            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <h3 className="text-lg font-bold text-white mb-2 flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-orange-500" />
                <span>How Points Work</span>
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm text-gray-400">
                <div>• 1,000 views = 10 points</div>
                <div>• 100 subscribers = 20 points</div>
                <div>• 1 episode = 5 points</div>
                <div>• 1 tip = 10 points</div>
                <div>• Weekly uploads = 50 bonus pts</div>
                <div>• High quality = +100 bonus pts</div>
              </div>
            </div>

            {/* Tier Cards */}
            {tiers && Object.keys(tiers).map((tierLevel) => {
              const tier = tiers[tierLevel];
              const TierIcon = TIER_ICONS[tierLevel];
              const isCurrent = parseInt(tierLevel) === currentTier;
              const isPast = parseInt(tierLevel) < currentTier;

              return (
                <div 
                  key={tierLevel}
                  className={`rounded-lg border-2 overflow-hidden transition-all ${
                    isCurrent 
                      ? 'border-orange-500 shadow-lg shadow-orange-500/20' 
                      : isPast 
                      ? 'border-green-500/30' 
                      : 'border-gray-700'
                  }`}
                  style={{
                    background: `linear-gradient(135deg, ${tier.color}10 0%, transparent 100%)`
                  }}
                >
                  {/* Tier Header */}
                  <div className="p-4 bg-gray-800/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${tier.color}30` }}
                        >
                          <TierIcon 
                            className="w-7 h-7" 
                            style={{ color: tier.color }}
                          />
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <h4 
                              className="text-xl font-black"
                              style={{ color: tier.color }}
                            >
                              {tier.name}
                            </h4>
                            {isCurrent && (
                              <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded">
                                YOUR TIER
                              </span>
                            )}
                            {isPast && (
                              <Check className="w-5 h-5 text-green-500" />
                            )}
                          </div>
                          <p className="text-sm text-gray-400">{tier.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Required</p>
                        <p className="text-lg font-bold text-white">
                          {tier.points_required.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">points</p>
                      </div>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="p-4 bg-gray-900/50">
                    <h5 className="text-xs font-bold text-gray-400 mb-3 uppercase">
                      Unlocked Features:
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {tier.features.map((feature) => {
                        const FeatureIcon = FEATURE_ICONS[feature] || Check;
                        const isLocked = parseInt(tierLevel) > currentTier;

                        return (
                          <div 
                            key={feature}
                            className={`flex items-center space-x-2 text-sm ${
                              isLocked ? 'text-gray-600' : 'text-gray-300'
                            }`}
                          >
                            {isLocked ? (
                              <Lock className="w-4 h-4 text-gray-600" />
                            ) : (
                              <FeatureIcon 
                                className="w-4 h-4" 
                                style={{ color: tier.color }}
                              />
                            )}
                            <span>{FEATURE_DESCRIPTIONS[feature] || feature}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-lg p-6 border border-orange-500/30 text-center">
              <h3 className="text-xl font-bold text-white mb-2">
                Keep Creating to Level Up! 🚀
              </h3>
              <p className="text-gray-400 text-sm">
                Upload consistently, engage with fans, and grow your audience to unlock powerful features
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TierSystemModal;
